import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, User, Phone, Envelope, Clock, X, Briefcase, Receipt, FileText } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function GlobalSearch({ onSelect, searchQuery, setSearchQuery, searchContext }) {
  // Use props for controlled search, fall back to local state for backward compatibility
  const [localQuery, setLocalQuery] = useState("");
  const query = searchQuery !== undefined ? searchQuery : localQuery;
  const setQuery = setSearchQuery || setLocalQuery;
  
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Check if we're doing context-aware local filtering (non-customer pages)
  const isLocalFilter = searchContext && searchContext.type !== "customers";
  
  // Get placeholder text based on context
  const placeholder = searchContext?.placeholder || "Search customers...";

  // Debounced search for customers (backend search)
  const searchCustomers = useCallback(async (searchQuery) => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API}/customers/search/global?q=${encodeURIComponent(searchQuery)}&limit=10`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce effect - only for customer search (backend)
  useEffect(() => {
    // If we're doing local filtering, don't do backend search
    if (isLocalFilter) {
      setResults([]);
      setTotal(0);
      setIsOpen(false);
      return;
    }
    
    const timer = setTimeout(() => {
      if (query.trim().length >= 2) {
        searchCustomers(query);
        setIsOpen(true);
      } else {
        setResults([]);
        setTotal(0);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, searchCustomers, isLocalFilter]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    } else if (e.key === "Enter" && results.length > 0 && !isLocalFilter) {
      // Navigate to first result (only for customer search)
      handleSelectCustomer(results[0]);
    }
  };

  const handleSelectCustomer = (customer) => {
    setIsOpen(false);
    setQuery("");
    navigate(`/customers/${customer.customer_id}`);
    if (onSelect) onSelect(customer);
  };

  const formatLastActivity = (timestamp) => {
    if (!timestamp) return null;
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    } catch {
      return null;
    }
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  // Get context icon
  const getContextIcon = () => {
    if (!searchContext) return null;
    switch (searchContext.type) {
      case "jobs":
        return <Briefcase size={14} className="text-[#FF6600]" />;
      case "invoices":
        return <Receipt size={14} className="text-[#FF6600]" />;
      case "estimates":
        return <FileText size={14} className="text-[#FF6600]" />;
      default:
        return null;
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid="global-search">
      {/* Search Input */}
      <div className="relative">
        <MagnifyingGlass
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => !isLocalFilter && query.length >= 2 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-64 pl-10 pr-8 py-2 text-sm border-2 border-zinc-200 rounded-lg focus:border-[#FF6600] focus:outline-none transition-colors"
          data-testid="global-search-input"
        />
        {/* Context indicator badge */}
        {isLocalFilter && query.length >= 2 && (
          <span className="absolute right-8 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-[#FF6600] bg-orange-50 px-1.5 py-0.5 rounded">
            {getContextIcon()}
            <span className="hidden sm:inline">Filtering</span>
          </span>
        )}
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-100 rounded"
            data-testid="clear-search-btn"
          >
            <X size={14} className="text-zinc-400" />
          </button>
        )}
      </div>

      {/* Results Dropdown - Only for customer search (backend) */}
      {isOpen && !isLocalFilter && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-zinc-200 rounded-lg shadow-lg z-50 overflow-hidden"
          data-testid="search-results-dropdown"
        >
          {loading ? (
            <div className="p-4 text-center text-sm text-zinc-400">
              <div className="animate-pulse">Searching...</div>
            </div>
          ) : results.length > 0 ? (
            <>
              <div className="max-h-80 overflow-y-auto">
                {results.map((customer) => (
                  <button
                    key={customer.customer_id}
                    onClick={() => handleSelectCustomer(customer)}
                    className="w-full px-4 py-3 text-left hover:bg-zinc-50 border-b border-zinc-100 last:border-0 transition-colors"
                    data-testid={`search-result-${customer.customer_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-zinc-400 flex-shrink-0" />
                          <span className="font-medium text-zinc-900 truncate">
                            {customer.name}
                          </span>
                        </div>
                        {customer.phone && (
                          <div className="flex items-center gap-2 mt-1">
                            <Phone size={12} className="text-zinc-300 flex-shrink-0" />
                            <span className="text-xs text-zinc-500">{customer.phone}</span>
                          </div>
                        )}
                        {customer.email && (
                          <div className="flex items-center gap-2 mt-0.5">
                            <Envelope size={12} className="text-zinc-300 flex-shrink-0" />
                            <span className="text-xs text-zinc-500 truncate">{customer.email}</span>
                          </div>
                        )}
                      </div>
                      {customer.last_activity && (
                        <div className="flex items-center gap-1 text-xs text-zinc-400 flex-shrink-0">
                          <Clock size={12} />
                          <span>{formatLastActivity(customer.last_activity)}</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {total > results.length && (
                <div className="px-4 py-2 text-xs text-center text-zinc-400 border-t border-zinc-100 bg-zinc-50">
                  Showing {results.length} of {total} results
                </div>
              )}
            </>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center text-sm text-zinc-400">
              No customers found for &ldquo;{query}&rdquo;
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
